import { useState, useEffect, FormEvent } from 'react';

const SUBMIT_GHL_URL = `https://kkmawmfkuffejnemowho.supabase.co/functions/v1/submit-to-ghl`;
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import SiteWidgets from '../../components/feature/SiteWidgets';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const contactDetails = [
  { icon: 'ri-phone-fill', label: 'Call us', value: '0330 010 1234', href: 'tel:03300101234', color: '#145A32' },
  { icon: 'ri-mail-fill', label: 'Email us', value: 'hello@renewablesmadeeasy.co.uk', href: 'mailto:hello@renewablesmadeeasy.co.uk', color: '#4CAF50' },
  { icon: 'ri-map-pin-2-fill', label: 'Areas covered', value: 'London · Essex · Suffolk · Cambridge', href: '#', color: '#7AC943' },
  { icon: 'ri-time-fill', label: 'Office hours', value: 'Mon–Fri 8am–6pm · Sat 9am–4pm', href: '#', color: '#145A32' },
];

const faqs = [
  { q: 'How quickly can you install?', a: 'Most installations are booked within 5–10 working days. Emergency repairs can often be arranged within 48 hours.' },
  { q: 'Do you offer finance?', a: 'Yes — we offer 0% finance options on solar and battery installations. Ask us for details when you get your quote.' },
  { q: 'Which areas do you cover?', a: 'We cover London, Essex, Suffolk and Cambridge. Enter your postcode on our homepage to instantly confirm coverage.' },
  { q: 'Are your engineers certified?', a: 'All our engineers are MCS-accredited for solar and battery, and OZEV-authorised for EV charger installations.' },
];

export default function ContactPage() {
  const [visible, setVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [error, setError] = useState('');
  const formAnim = useScrollAnimation();
  const faqAnim = useScrollAnimation();

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Please fill in name, email and message.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const formBody = new URLSearchParams({ ...form });
      const ghlPayload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        source: 'Contact Form',
        tags: ['contact-form'],
        service: form.service,
        message: form.message,
      };
      await Promise.allSettled([
        fetch('https://readdy.ai/api/form/d74523cprit363gl0hig', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formBody.toString(),
        }),
        fetch(SUBMIT_GHL_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(ghlPayload),
        }),
      ]);
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-white font-sans">
      <Header />

      {/* Hero */}
      <section className="bg-[#145A32] pt-24 md:pt-40 pb-12 md:pb-16 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/2 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-56 h-56 rounded-full bg-white/5 translate-y-1/2 -translate-x-1/2 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
          <Link to="/" className="inline-flex items-center gap-1 text-white/60 hover:text-white text-sm mb-6 transition-colors cursor-pointer">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line text-sm" /></div>
            Back to home
          </Link>
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="text-[#7AC943] text-xs font-bold uppercase tracking-widest mb-3">GET IN TOUCH</div>
            <h1 className="font-heading font-black text-white text-4xl md:text-5xl leading-tight mb-4">
              Contact us
            </h1>
            <p className="text-white/70 text-base md:text-lg max-w-xl leading-relaxed">
              Got a question about solar, battery, EV charging or a repair? We&apos;re here to help. Fill in the form or give us a call.
            </p>
          </div>

          {/* Contact cards */}
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            {contactDetails.map((c, i) => (
              <a
                key={c.label}
                href={c.href}
                style={{ transitionDelay: `${i * 80}ms` }}
                className="bg-white/10 hover:bg-white/20 rounded-xl p-4 flex items-start gap-3 transition-all duration-200 cursor-pointer group"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 flex-shrink-0 group-hover:bg-white/20 transition-colors">
                  <i className={`${c.icon} text-[#7AC943] text-lg`} />
                </div>
                <div>
                  <div className="text-white/60 text-xs uppercase tracking-wide mb-0.5">{c.label}</div>
                  <div className="text-white font-bold text-sm leading-snug">{c.value}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Form + FAQ */}
      <section className="py-14 md:py-20 bg-[#f7f8fa]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">

            {/* Contact form */}
            <div
              ref={formAnim.ref}
              className={`bg-white rounded-2xl p-6 md:p-8 transition-all duration-700 ${formAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 animate-scale-in">
                    <i className="ri-checkbox-circle-fill text-[#7AC943] text-6xl" />
                  </div>
                  <h3 className="font-heading font-bold text-2xl text-[#1F2933] mb-2">Message sent!</h3>
                  <p className="text-gray-500 text-sm leading-relaxed max-w-xs mx-auto">
                    Thanks for getting in touch. We&apos;ll get back to you within 2 working hours.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', service: '', message: '' }); }}
                    className="mt-6 text-[#4CAF50] text-sm font-semibold underline cursor-pointer"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="font-heading font-bold text-2xl text-[#1F2933] mb-1">Send us a message</h2>
                  <p className="text-gray-500 text-sm mb-6">We respond within 2 working hours, Monday–Saturday.</p>

                  <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <input
                        type="text"
                        name="name"
                        placeholder="Your name *"
                        value={form.name}
                        onChange={handleChange}
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#4CAF50] transition-colors"
                      />
                      <input
                        type="email"
                        name="email"
                        placeholder="Email address *"
                        value={form.email}
                        onChange={handleChange}
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#4CAF50] transition-colors"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <input
                        type="tel"
                        name="phone"
                        placeholder="Phone number"
                        value={form.phone}
                        onChange={handleChange}
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#4CAF50] transition-colors"
                      />
                      <div className="relative">
                        <select
                          name="service"
                          value={form.service}
                          onChange={handleChange}
                          className="w-full border-2 border-gray-200 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#4CAF50] transition-colors cursor-pointer appearance-none bg-white"
                        >
                          <option value="">Service interest</option>
                          <option value="Solar Installation">Solar Installation</option>
                          <option value="Battery Storage">Battery Storage</option>
                          <option value="Solar + Battery Bundle">Solar + Battery Bundle</option>
                          <option value="EV Charger">EV Charger</option>
                          <option value="Repair / Maintenance">Repair / Maintenance</option>
                          <option value="General Enquiry">General Enquiry</option>
                        </select>
                        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-down-s-line text-gray-400" />
                        </div>
                      </div>
                    </div>
                    <textarea
                      name="message"
                      placeholder="Your message *"
                      value={form.message}
                      onChange={handleChange}
                      rows={5}
                      maxLength={500}
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#4CAF50] transition-colors resize-none"
                    />
                    <div className="text-right text-xs text-gray-400">{form.message.length}/500</div>

                    {error && <p className="text-red-500 text-xs bg-red-50 rounded-lg px-3 py-2">{error}</p>}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full shimmer-btn text-white font-bold py-4 rounded-xl text-sm hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-70"
                    >
                      {submitting ? 'Sending...' : 'Send message →'}
                    </button>
                  </form>
                </>
              )}
            </div>

            {/* FAQ + extra info */}
            <div
              ref={faqAnim.ref}
              className={`transition-all duration-700 delay-100 ${faqAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <div className="text-xs font-bold uppercase tracking-widest text-[#4CAF50] mb-2">COMMON QUESTIONS</div>
              <h2 className="font-heading font-bold text-2xl text-[#1F2933] mb-6">Quick answers</h2>

              <div className="flex flex-col gap-3 mb-10">
                {faqs.map((faq, i) => (
                  <div key={i} className="border border-gray-100 rounded-xl overflow-hidden bg-white">
                    <button
                      className="w-full flex items-center justify-between p-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    >
                      <span className="font-semibold text-[#1F2933] text-sm pr-4">{faq.q}</span>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-[#4CAF50] text-lg`} />
                      </div>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-40' : 'max-h-0'}`}>
                      <p className="px-4 pb-4 text-gray-500 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Emergency / Direct CTA */}
              <div className="bg-[#145A32] rounded-2xl p-6 text-white">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10">
                    <i className="ri-phone-fill text-[#7AC943] text-xl" />
                  </div>
                  <div>
                    <div className="font-bold text-base">Prefer to talk?</div>
                    <div className="text-white/70 text-xs">Mon–Sat, 8am–6pm</div>
                  </div>
                </div>
                <a
                  href="tel:03300101234"
                  className="block w-full text-center bg-[#7AC943] hover:bg-[#6ab538] text-white font-bold py-3 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                >
                  Call 0330 010 1234
                </a>
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('openBooking'))}
                  className="mt-2 block w-full text-center bg-white/10 hover:bg-white/20 text-white font-bold py-3 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                >
                  Book an appointment →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <SiteWidgets />
    </main>
  );
}
